// Dashboard.jsx — 신규 라우트로 redirect만 수행 (Step 6, 2026-04-27)
//
// 변경 이력:
//   기존: 탭 시스템 (TabBar + AttendanceSection + LeavesSection + AdminTabsSection 등) 300줄
//   신규: ?tab= query 파라미터를 신규 /my/* 또는 /admin/* 라우트로 redirect (~80줄)
//
// 처리 흐름:
//   1. URL ?imp_token=XXX 파라미터 → apiClient.initImpersonation 저장 (위장 접속 새 탭 진입)
//   2. URL ?tab=XXX 파라미터 → STATIC_MAP 또는 동적 패턴 매칭 → 신규 라우트로 navigate
//   3. tab 없음 또는 매칭 실패 → /my/attendance (default)
//
// 폐기된 의존성: useDashboardBase / useDashboardActions / useTabsBuild / TabBar / AdminTabsSection
//   → Step 7에서 deprecated 주석 + 검증 후 파일 삭제
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '../../api/apiClient';
import { usePermissions } from '../../hooks/usePermissions';
import { TAB_KEYED_FEATURES, companyFeaturePath } from '../admin/adminlayout/companyFeatures';

// 정적 tab 키 → 신규 라우트 매핑 (TAB_CONFIG 33개 기준)
const STATIC_MAP = {
  // 직원 본인
  attendance: '/my/attendance',
  leaves: '/my/leaves',
  payslips: '/my/payslips',
  tm: '/my/tm-calls',
  'tm-jeju': '/my/tm-calls-jeju', // 라우트 폐기됐지만 키 유지 (옛 dashboard 진입 호환)
  // 2026-04-29: tm-ext-call 폐기 → DOT/LNJ 분리. 옛 키는 DOT 으로 fallback (안전 매핑)
  'tm-ext-call': '/my/tm-calls-dot',
  'tm-call-dot': '/my/tm-calls-dot',
  'tm-call-lnj': '/my/tm-calls-lnj',
  settlement: '/my/settlement',
  'settlement-dot': '/my/settlement-dot',
  'settlement-lnj': '/my/settlement-lnj',
  'tm-contracts': '/my/contracts',
  'tm-recent-contracts': '/my/recent-contracts',
  summary: '/my/summary',
  'meeting-map': '/my/meeting-map',
  'region-coverage': '/my/region-coverage',
  // 관리자 (이미 존재)
  'leave-admin': '/admin/leaves',
  'attendance-admin': '/admin/attendance',
  'attendance-calendar': '/admin/attendance?view=calendar', // 2026-09-29 출퇴근 관리 [달력] 탭으로 합침
  users: '/admin/users',
  ips: '/admin/attendance', // 2026-10-23 IP 관리 → 출퇴근 관리 ☰
  'tm-admin': '/admin/tm-calls',
  'tm-jeju-admin': '/admin/tm-calls-jeju',
  'settlement-management': '/admin/settlement-management',
  'meeting-map-admin': '/admin/meeting-map',
  contracts: '/admin/contracts',
  quotes: '/admin/quotes',
  revenue: '/admin/revenue',
  promotional: '/admin/promotional',
  'promotional-cost': '/admin/promotional-cost',
  'promotional-monthly-settlement': '/admin/promotional-monthly-settlement',
  'ticket-types': '/admin/ticket-types',
  db: '/admin/db',
  logs: '/admin/logs',
  'employee-view': '/admin/employee-view',
  'work-assignment': '/admin/work-feature-assignments',
  'emergency-team': '/admin/emergency-team',
  reservations: '/admin/reservations',
  'reservation-revenue': '/admin/reservation-revenue',
};

// 회사별 옛 링크(ext-*-CODE) → 지금 주소. **주소는 companyFeatures.js 표가 정한다** (2026-09-11 C2).
//   전에는 여기 9줄이 손으로 적혀 있었고, 그중 정산 관리 한 줄만 옛 주소
//   /admin/payout-management-{회사} 를 가리켜 **도트·엘엔제이 외 회사는 홈으로 튕겼다**
//   (실측: ?tab=ext-payout-SUNCREAM → /admin/home). 메뉴 쪽은 2026-09-10 에 고쳤는데
//   이 경로만 남아 있었다. 표를 읽게 해 세 번째 사본을 없앤다.
function extTabToRoute(tab) {
  for (const f of TAB_KEYED_FEATURES) {
    const m = tab.match(new RegExp(`^${f.tabKey}-([A-Za-z0-9]+)$`));
    if (m) return companyFeaturePath(m[1].toUpperCase(), f);
  }
  return null;
}

// 동적 패턴 매칭 (integrated-N / ext-*-CODE)
function tabToRoute(tab) {
  if (!tab) return null;
  if (STATIC_MAP[tab]) return STATIC_MAP[tab];
  let m;
  if ((m = tab.match(/^integrated-(\d+)$/)))                  return `/admin/integrated/${m[1]}`;
  return extTabToRoute(tab);
}

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [redirectData, setRedirectData] = useState({
    loaded: false,
    explicitTarget: null,
    me: null,
    permCodes: [],
    taskCodes: [],
    featureSetNames: [],
  });
  const permCodesSet = useMemo(() => new Set(redirectData.permCodes || []), [redirectData.permCodes]);
  const permissions = usePermissions({
    permCodes: permCodesSet,
    taskCodes: redirectData.taskCodes,
    featureSetNames: redirectData.featureSetNames,
    workerType: redirectData.me?.workerType,
    externalCompany: redirectData.me?.externalCompany,
    role: redirectData.me?.role,
    callCompanyCount: redirectData.me?.callCompanyCount, // A '(통합)' 는 회사 1곳 테넌트만 첫 화면 후보(2026-10-14)
  });

  useEffect(() => {
    let active = true;
    const params = new URLSearchParams(location.search);
    // 1. 위장 접속 토큰 처리 (새 탭 진입)
    const impToken = params.get('imp_token');
    if (impToken) {
      apiClient.initImpersonation(impToken);
      // URL에서 imp_token 제거 (history pollution 방지)
      params.delete('imp_token');
      const newSearch = params.toString();
      window.history.replaceState(null, '', `${location.pathname}${newSearch ? `?${newSearch}` : ''}`);
    }
    // 2. tab 파라미터 → 신규 라우트
    const tab = params.get('tab');
    const explicitTarget = tabToRoute(tab);

    if (explicitTarget) {
      setRedirectData((prev) => ({ ...prev, loaded: true, explicitTarget }));
      return () => { active = false; };
    }

    (async () => {
      const [meRes, taskRes, permRes] = await Promise.all([
        apiClient.get('/api/me'),
        apiClient.get('/api/my/work-feature-assignments'),
        apiClient.get('/api/my/permissions'),
      ]);
      if (!active) return;

      setRedirectData({
        loaded: true,
        explicitTarget: null,
        me: meRes.ok ? meRes.data?.user || null : null,
        taskCodes: taskRes.ok ? taskRes.data?.taskCodes || [] : [],
        featureSetNames: taskRes.ok ? taskRes.data?.featureSetNames || [] : [],
        permCodes: permRes.ok && Array.isArray(permRes.data?.permCodes) ? permRes.data.permCodes : [],
      });
    })();

    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!redirectData.loaded) return;
    const fallbackMenu = permissions.resolveFirstAvailableMenu();
    const target = redirectData.explicitTarget || tabToRoute(fallbackMenu) || '/my/attendance';
    navigate(target, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectData.loaded, redirectData.explicitTarget]);
  return <div style={{ padding: 24, color: 'var(--text-muted)', textAlign: 'center' }}>이동 중...</div>;
}
